import { format } from 'date-fns';
import { FC, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, InfoCircle } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../../components/hover-card/index';
import { PlaceholderBar } from '../../../../components/loading-skeleton/index';
import { ExpandedTableRowsPanel } from '../../../../components/table-animations/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { useSelectedPoolsStore } from '../../../../context/selected-pools.store';
import { Company, SharePlan } from '../../../../hooks/index';
import { sharePlanTypeTitle, SharePlanUnionType } from '../../../../types/pool-plans.types';
import { PoolPlansPopover } from '../pool-plans-popover';

// Skeleton rows shown inside an expanded plan while its stakeholders lazy-load.
const StickyPlanStakeholderSkeleton: FC = () => (
  <tr className="flex h-[72px] w-full animate-pulse border-l-2 border-brand-700 bg-white">
    <td className="w-[49px] bg-gray-50" />
    <td className="flex h-[72px] flex-1 items-center gap-3 pb-4 pl-12 pr-4 pt-4">
      <PlaceholderBar className="size-10 shrink-0 rounded-full bg-gray-100" />
      <PlaceholderBar className="h-4 w-40 rounded bg-gray-100" />
    </td>
  </tr>
);

const ScrollablePlanStakeholderSkeleton: FC = () => (
  <tr className="h-[72px] w-full animate-pulse bg-white">
    <td className="p-4 text-sm text-gray-300">-</td>
    <td className="p-4 text-sm text-gray-300">-</td>
    {Array.from({ length: 8 }).map((_, index) => (
      <td className="p-4" key={index}>
        <PlaceholderBar className="h-4 w-16 rounded bg-gray-100" />
      </td>
    ))}
    <td className="relative" />
  </tr>
);

type StickyPlanItemProps = {
  date: Date;
  isSelected: boolean;
  plan: SharePlanUnionType;
};

export const StickyPlanItem: FC<StickyPlanItemProps> = ({ plan, isSelected, date }) => {
  const { selectedCompanyId } = Company.useSelected();

  const { setSelectedPlan, deselectPlan } = useSelectedPoolsStore();
  const [isExpandedVisual, setExpandedVisual] = useState(false);

  const { stakeholders: planStakeholders, isLoading } = SharePlan.useStakeholders({
    companyId: selectedCompanyId,
    planId: plan.id,
    vestByDate: date,
    enabled: isSelected || isExpandedVisual,
  });

  const hasStakeholders = (plan.granted ?? 0) > 0;

  const handlePlanUnselected = () => {
    deselectPlan(plan.id);
  };

  const handlePlanSelected = () => {
    setSelectedPlan(plan.id);
  };

  const isTerminatedDatePassed = useMemo(() => {
    return plan?.terminationDate
      ? new Date(plan.terminationDate).getTime() < new Date().getTime()
      : false;
  }, [plan?.terminationDate]);

  return (
    <>
      <tr
        className={twMerge(
          'flex h-[72px] w-full border-l-[2px] border-l-brand-700 bg-white transition-colors',
          isSelected && 'bg-brand-25',
        )}
        key={plan?.id}
      >
        <td className="relative h-[72px] w-[49px] border-b-[1px] border-b-gray-100 bg-gray-50">
          <div className="flex h-full w-full items-center justify-center">
            <div className="w-[49px]">
              {hasStakeholders && (
                <Button
                  className="h-[72px] cursor-pointer p-4 transition-colors"
                  onClick={isSelected ? handlePlanUnselected : handlePlanSelected}
                  styleType="NONE"
                >
                  <ChevronDownIcon
                    className={twMerge('transition-transform', isSelected ? 'rotate-180' : '')}
                    iconColor={isSelected ? '#2565C8' : '#344054'}
                  />
                </Button>
              )}
            </div>
          </div>
        </td>
        <td
          className={twMerge(
            'flex h-[72px] w-full items-center gap-4 text-nowrap border-b-[1px] border-b-gray-100 pb-4 pl-10 pr-6 pt-4',
            plan?.isTerminated && isTerminatedDatePassed && 'justify-between',
          )}
        >
          <div className="flex flex-col">
            <Tooltip content={plan.name}>
              <span className="max-w-[300px] truncate text-sm font-[450] text-gray-900">
                {plan.name}
              </span>
            </Tooltip>

            <span className="max-w-[300px] truncate text-xs font-[450] text-gray-400">
              <AppFormattedMessage id={sharePlanTypeTitle[plan?.type]} />
            </span>
          </div>
          {plan.isTerminated && (
            <>
              {isTerminatedDatePassed ? (
                <span className="rounded-2xl bg-fireside-50 px-2 py-[2px] text-label-sm font-[450] text-fireside-600">
                  <AppFormattedMessage id="TERMINATED" />
                </span>
              ) : (
                <HoverCard>
                  <HoverCardTrigger>
                    <InfoCircle className="size-4 cursor-pointer" />
                  </HoverCardTrigger>
                  <HoverCardContent
                    className="z-20 w-max rounded-lg bg-[#101828] px-3 py-2"
                    isShownTriangle
                    side="top"
                    triangleClassName="bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 ml-0"
                  >
                    <span className="text-label-md font-[550] text-white">
                      <AppFormattedMessage id="TERMINATION_SET" />{' '}
                      {plan?.terminationDate && format(plan?.terminationDate, 'dd/MM/yyyy')}
                    </span>
                  </HoverCardContent>
                </HoverCard>
              )}
            </>
          )}
        </td>
      </tr>
      {hasStakeholders && (
        <ExpandedTableRowsPanel
          contentKey={isLoading ? 'loading' : 'loaded'}
          isExpanded={isSelected}
          onExpandedVisualChange={setExpandedVisual}
          syncColumns={false}
        >
          {(isSelected || isExpandedVisual) && isLoading ? (
            <StickyPlanStakeholderSkeleton />
          ) : (
            (isSelected || isExpandedVisual) &&
            planStakeholders.map((stakeholder) => (
              <tr
                className="h-[72px] w-full border-l-2 border-brand-700 bg-white transition-colors"
                key={stakeholder.id}
              >
                <td className="w-[49px] bg-gray-50">
                  <div className="flex h-full w-full cursor-pointer items-center justify-center"></div>
                </td>
                <td className="flex w-fit items-center gap-4 overflow-hidden text-nowrap pb-4 pl-12 pr-4 pt-4">
                  <div className="flex max-w-[300px] items-center gap-3">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm font-[450] text-gray-700">
                      {stakeholder.fullName[0]}
                    </div>
                    <Tooltip content={stakeholder.fullName}>
                      <span className="block min-w-0 max-w-[300px] truncate text-sm font-[450] text-gray-700">
                        {stakeholder.fullName}
                      </span>
                    </Tooltip>
                  </div>
                </td>
              </tr>
            ))
          )}
        </ExpandedTableRowsPanel>
      )}
    </>
  );
};

type ScrollablePlanItemProps = {
  date: Date;
  isSelected: boolean;
  onDeletePlanClick: (id: string) => void;
  onEditPlanClick: (id: string) => void;
  onEditPlanTerminationDateClick: (id: string) => void;
  onViewPlanClick: (id: string) => void;
  plan: SharePlanUnionType;
};

export const ScrollablePlanItem: FC<ScrollablePlanItemProps> = ({
  plan,
  isSelected,
  onEditPlanClick,
  onViewPlanClick,
  onDeletePlanClick,
  onEditPlanTerminationDateClick,
  date,
}) => {
  const { selectedCompanyId } = Company.useSelected();
  const { stakeholders, isLoading } = SharePlan.useStakeholders({
    companyId: selectedCompanyId,
    planId: plan.id,
    vestByDate: date,
    enabled: isSelected,
  });

  const hasStakeholders = (plan.granted ?? 0) > 0;

  const { approveHurdleGrants: vestingTasks } = SharePlan.useSharePlanGrantItems({
    companyId: selectedCompanyId,
    sharePlanId: plan.id,
  });
  const [isExpandedVisual, setExpandedVisual] = useState(false);

  const granted = plan.granted?.toLocaleString('en-US');
  const totalGrantVested = plan.totalGrantVested?.toLocaleString('en-US');
  const sharesVested = plan.sharesVested?.toLocaleString('en-US');
  const exercised = plan.exercised?.toLocaleString('en-US');
  const expired = plan.expired?.toLocaleString('en-US');
  const exercisable = plan.exercisable?.toLocaleString('en-US');
  const cashSettled = plan.cashSettled?.toLocaleString('en-US');
  const returnedToPool = plan.returnedToPool?.toLocaleString('en-US');

  const isTerminatedDatePassed = useMemo(() => {
    return plan?.terminationDate
      ? new Date(plan.terminationDate).getTime() < new Date().getTime()
      : false;
  }, [plan?.terminationDate]);

  return (
    <>
      <tr
        className={twMerge(
          'h-[72px] w-full border-b-[1px] border-b-gray-100 bg-white transition-colors',
          isSelected && 'bg-brand-25',
        )}
      >
        <td className="p-4 text-sm text-gray-300">-</td>
        <td className="p-4 text-sm text-gray-300">-</td>
        <td className="overflow-hidden p-4">
          <span className={twMerge('max-w-[100px] truncate text-sm text-gray-700')}>{granted}</span>
        </td>
        <td className="overflow-hidden p-4">
          <span className={twMerge('max-w-[100px] truncate text-sm text-gray-700')}>
            {totalGrantVested}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span className={twMerge('max-w-[100px] truncate text-sm text-gray-700')}>
            {sharesVested}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span className={twMerge('max-w-[100px] truncate text-sm text-gray-700')}>
            {exercisable}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span className={twMerge('max-w-[100px] truncate text-sm text-gray-700')}>
            {exercised}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span className={twMerge('max-w-[100px] truncate text-sm text-gray-700')}>
            {cashSettled}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span className={twMerge('max-w-[100px] truncate text-sm text-gray-700')}>
            {returnedToPool}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span className={twMerge('max-w-[100px] truncate text-sm text-gray-700')}>{expired}</span>
        </td>
        <td className="relative">
          <PoolPlansPopover
            canBeDeleted={vestingTasks?.length === 0}
            date={date}
            id={plan.id}
            isSharePlan={true}
            isTerminated={isTerminatedDatePassed}
            onDeleteClick={onDeletePlanClick}
            onEditClick={onEditPlanClick}
            onEditTerminationDateClick={onEditPlanTerminationDateClick}
            onViewClick={onViewPlanClick}
            sharePlans={vestingTasks}
            text={<AppFormattedMessage id="PLAN" />}
          />
        </td>
      </tr>
      {hasStakeholders && (
        <ExpandedTableRowsPanel
          contentKey={isLoading ? 'loading' : 'loaded'}
          isExpanded={isSelected}
          onExpandedVisualChange={setExpandedVisual}
        >
          {(isSelected || isExpandedVisual) && isLoading ? (
            <ScrollablePlanStakeholderSkeleton />
          ) : (
            (isSelected || isExpandedVisual) &&
            stakeholders.map(
              ({
                granted,
                totalGrantVested,
                sharesVested,
                exercisable,
                exercised,
                expired,
                id,
                cashSettled,
                returnedToPool,
              }) => (
                <tr className="h-[72px] w-full bg-white" key={id}>
                  <td className="p-4 text-sm text-gray-300">-</td>
                  <td className="p-4 text-sm text-gray-300">-</td>
                  <td className="overflow-hidden p-4">
                    <span className="max-w-[100px] truncate text-sm text-gray-700">
                      {granted?.toLocaleString('en-US')}
                    </span>
                  </td>
                  <td className="overflow-hidden p-4">
                    <span className="max-w-[100px] truncate text-sm text-gray-700">
                      {totalGrantVested?.toLocaleString('en-US')}
                    </span>
                  </td>
                  <td className="overflow-hidden p-4">
                    <span className="max-w-[100px] truncate text-sm text-gray-700">
                      {sharesVested?.toLocaleString('en-US')}
                    </span>
                  </td>
                  <td className="overflow-hidden p-4">
                    <span className="max-w-[100px] truncate text-sm text-gray-700">
                      {exercisable?.toLocaleString('en-US')}
                    </span>
                  </td>
                  <td className="overflow-hidden p-4">
                    <span className="max-w-[100px] truncate text-sm text-gray-700">
                      {exercised?.toLocaleString('en-US')}
                    </span>
                  </td>
                  <td className="overflow-hidden p-4">
                    <span className="max-w-[100px] truncate text-sm text-gray-700">
                      {cashSettled?.toLocaleString('en-US')}
                    </span>
                  </td>
                  <td className="overflow-hidden p-4">
                    <span className="max-w-[100px] truncate text-sm text-gray-700">
                      {returnedToPool?.toLocaleString('en-US')}
                    </span>
                  </td>
                  <td className="overflow-hidden p-4">
                    <span className="max-w-[100px] truncate text-sm text-gray-700">
                      {expired?.toLocaleString('en-US')}
                    </span>
                  </td>
                  <td className="relative"></td>
                </tr>
              ),
            )
          )}
        </ExpandedTableRowsPanel>
      )}
    </>
  );
};
